import { graphqlPost } from "api/graphqlClient";
import { Button } from "components/Button/Button";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getDashboardViewLink } from "utils/service";

import styles from "./MobileInterstitial.module.scss";

interface DashboardRow {
    id: string;
    name: string;
}

interface MobileInterstitialProps {
    /** Called when the user chooses to continue on mobile anyway. */
    onContinue: () => void;
}

/**
 * Narrow-viewport gate shown in place of the dense desktop-first surfaces. Tells
 * the user the app is not optimized for mobile, offers the workspace dashboards
 * (the mobile-friendly view) as tappable links, and lets them continue on mobile
 * anyway (persisted for the session by the caller).
 *
 * See operator-task-fe-mobile-not-optimized-interstitial.
 */
export const MobileInterstitial = ({ onContinue }: MobileInterstitialProps) => {
    const [dashboards, setDashboards] = useState<DashboardRow[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const isMounted = useRef<boolean>(true);

    useEffect(() => {
        isMounted.current = true;
        // Dashboards the current user can read (the API scopes _plain_list by the
        // caller's permissions), ordered by name for a stable list.
        const query = `query MobileDashboardList($order: String) {
            dashboard_plain_list(order: $order) {
                id
                name
            }
        }`;
        graphqlPost({ query, variables: { order: "+name" } })
            .then((response) => {
                if (!isMounted.current) {
                    return;
                }
                const data = response?.data?.dashboard_plain_list;
                setDashboards(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch(() => {
                if (isMounted.current) {
                    setDashboards([]);
                    setLoading(false);
                }
            });
        return () => {
            isMounted.current = false;
        };
    }, []);

    return (
        <div className={styles.mobileInterstitial} role="region" aria-label="Not optimized for mobile">
            <div className={styles.card}>
                <h1 className={styles.heading}>This app is not optimized for mobile</h1>
                <p className={styles.subtext}>
                    Open it in a desktop browser to use all features.
                </p>

                {!loading && dashboards.length > 0 && (
                    <div className={styles.dashboards}>
                        <div className={styles.dashboardsHeading}>Available dashboards:</div>
                        <ul className={styles.dashboardList}>
                            {dashboards.map((dashboard) => (
                                <li key={dashboard.id}>
                                    <Link
                                        className={styles.dashboardLink}
                                        to={getDashboardViewLink(dashboard.name, "name")}
                                    >
                                        {dashboard.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className={styles.actions}>
                    <Button
                        label="Continue on mobile anyway"
                        variation="white"
                        onClick={onContinue}
                    />
                </div>
            </div>
        </div>
    );
};

export default MobileInterstitial;
